import { InjectedDependencies, StoreI } from 'ducks/store';
import { createSelector, Selector } from 'reselect';
import { Observable, of, merge } from 'rxjs';
import { ofType } from 'redux-observable';
import { mergeMap, catchError, map } from 'rxjs/operators';
import { Action, createAction, handleActions } from 'redux-actions';

export type EmojiesStore = Record<string, number>;

export type EmojiesState = {
    lastUserEmoji: number | null;
    userEmojiToShow: number | null;
    queue: string[];
    enabled: boolean | 'unavailable'
    online: boolean;
};

const lsKey = 'KHURAL_EMOJIES_KEY';

const getInitialValue = () => {
    try {
        const value = JSON.parse(localStorage.getItem(lsKey));
        return value ?? true;
    } catch (e) {}

    return false;
};

const setStoreItem = (enabled: boolean) => {
    try {
        localStorage.setItem(lsKey, String(enabled));
    } catch {}
};

export const initialState: EmojiesState = {
    lastUserEmoji: null,
    userEmojiToShow: null,
    queue: [],
    enabled: 'unavailable',
    online: false,
};

export const sendEmoji = createAction<number>('emojies/SEND');
export const showUserEmoji = createAction<number>('emojies/SHOW');
export const applyEmojies = createAction<EmojiesStore>('emojies/APPLY');
export const toggleEmojies = createAction<boolean | 'unavailable' | 'available'>('emojies/TOGGLE');
export const toggleOnline = createAction<boolean>('emojies/ONLINE');
export const pingEmoji = createAction('emojies/PING');
export const resetUserEmoji = createAction('emojies/RESET_USER_EMOJI');

export const reducer = handleActions<EmojiesState, EmojiesStore | number | boolean | 'unavailable' | 'available'>({
    'emojies/RESET_USER_EMOJI': (state: EmojiesState): EmojiesState => {
        return { ...state, lastUserEmoji: null };
    },
    'emojies/SEND': (state: EmojiesState, action: Action<number>): EmojiesState => {
        return { ...state, lastUserEmoji: action.payload };
    },
    'emojies/SHOW': (state: EmojiesState, action: Action<number>): EmojiesState => {
        return { ...state, userEmojiToShow: action.payload };
    },
    'emojies/APPLY': (state: EmojiesState, action: Action<EmojiesStore>): EmojiesState => {
        const { lastUserEmoji } = state;
        const { payload } = action;

        const bigEmojiCount = 10;
        const queue: string[] = Object.keys(payload).reduce((acc, curr) => {
            let count = Math.min(payload[curr], 100);

            if (parseInt(curr, 10) === lastUserEmoji && count > 0) {
                count = count - 1;
            }

            // Если количество > bigEmojiCount эмодзи, то отображаем одну большую эмодзю по индексу + 14
            const emojiIndex = count > bigEmojiCount ? parseInt(curr, 10) + 14 : curr;
            const emojieArray = new Array(count > bigEmojiCount ? Math.floor(count / bigEmojiCount) : count)
                .fill(emojiIndex.toString());

            return acc.concat(emojieArray);
        }, []);

        return {
            ...state,
            queue,
            lastUserEmoji: null,
        };
    },
    'emojies/TOGGLE': (state: EmojiesState, action: Action<boolean | 'unavailable' | 'available'>): EmojiesState => {
        const { payload } = action;

        if (payload !== 'unavailable') {
            if (payload === 'available') {
                setStoreItem(getInitialValue());
            } else {
                setStoreItem(payload);
            }
        }

        return { ...state, enabled: payload === 'available' ? getInitialValue() : payload, queue: [] };
    },
    'emojies/ONLINE': (state: EmojiesState, action: Action<boolean>): EmojiesState => {
        const { payload } = action;

        return { ...state, online: payload, lastUserEmoji: null };
    },
}, initialState,
);

export const selectEmojiesState: Selector<StoreI, EmojiesState> =
    (store): EmojiesState => store.emojies || initialState;

export const selectEmojiesToSend = createSelector(
    selectEmojiesState,
    ({ queue }) => queue,
);

export const selectEmojiesEnabled = createSelector(
    selectEmojiesState,
    ({ enabled }) => enabled,
);

export const selectIsVideoOnline = createSelector(
    selectEmojiesState,
    ({ online }) => online,
);

export const selectUserEmoji = createSelector(
    selectEmojiesState,
    ({ userEmojiToShow }) => userEmojiToShow,
);

export const epic = (action$: Observable<Action<string>>, $state: StoreI, deps: InjectedDependencies):
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Observable<Action<any>> => {
    const { emojiesApi: { sendEmojiRequest, pingEmojiRequest } } = deps;

    const sendEmoji$ = action$.pipe(
        ofType(sendEmoji.toString()),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mergeMap(({ payload }: any) =>
            sendEmojiRequest(payload).pipe(
                map((response: string) =>
                    applyEmojies(JSON.parse(response)),
                ),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                catchError((error: any) => of({
                    type: 'EMOJIES_ERROR',
                    payload: error.message || 'Failed to send emoji',
                })),
            ),
        ),
    );

    const pingEmoji$ = action$.pipe(
        ofType(pingEmoji.toString()),
        mergeMap(() =>
            pingEmojiRequest().pipe(
                map(response => {
                    //@ts-ignore
                    if (response?.error) {
                        return toggleEmojies('unavailable');
                    }
                    return toggleEmojies('available');
                }),
                catchError(() => of(toggleEmojies('unavailable'))),
            ),
        ),
    );

    return merge(sendEmoji$, pingEmoji$);
};
